import { HttpClient } from '@angular/common/http';
import { DefaultDataServiceConfig } from '@ngrx/data';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface User {
  uuid?: string;
  created?: string;
  first_name: string;
  last_name: string;
  email: string;
  occupation?: string;
  status?: string;
  licenses?: string[];
  account?: string;
  is_admin?: boolean;
}

export const LicenseTypes: any = [
  {
    code: 'admin',
    name: 'Admin',
    selectName: 'Admin Only',
    avatar: 'A',
    items: [],
  },
  {
    code: 'premier',
    name: 'Premier',
    selectName: null,
    avatar: 'P',
    items: [
      'Unlimited therapy room use',
      'Caseload management',
      'Administrative panel',
      'Assessment achievement',
      'Session documentation',
      'Premium content unlocked',
    ],
  },
  {
    code: 'premier-aa',
    name: 'Premier AA',
    selectName: null,
    avatar: 'AA',
    items: [
      'Unlimited therapy room use',
      'Caseload management',
      'Administrative panel',
      'Assessment achievement',
      'Session documentation',
      'Premium content unlocked',
      'Unlimited assessments access',
    ],
  },
  {
    code: 'pro',
    name: 'Pro',
    selectName: null,
    avatar: 'PRO',
    items: [
      'Unlimited therapy room use',
      'Caseload management',
      'Administrative panel',
      'Assessment achievement',
      'Session documentation',
      'Premium content unlocked',
      '50 uses of any assessments',
    ],
  },
  {
    code: 'elite',
    name: 'Elite',
    selectName: null,
    avatar: 'E',
    items: [
      'Unlimited therapy room use',
      'Caseload management',
      'Administrative panel',
      'Assessment achievement',
      'Session documentation',
      'Premium content unlocked',
      'Unlimited assessments access',
    ],
  },
  {
    code: 'starter',
    name: 'Starter',
    selectName: null,
    avatar: 'S',
    items: [
      '8 x 30m therapy sessions/month',
      'Caseload management',
      'Administrative panel',
      'Session documentation',
    ],
  },
  {
    code: 'remarkable_reading',
    name: 'Remarkable Reading',
    selectName: null,
    avatar: 'R',
    items: [
      '60 hour training in delivering the structured literacy program, OG+',
      'Unlimited access to OG + curriculum',
      'Access to premium library content',
    ],
  },
  {
    // fake "Unassigned" license
    code: 'unassigned',
    name: 'Unassign',
    avatar: '-',
    items: [],
  },
];

export const LicenseTypeMap: any = LicenseTypes.reduce(
  (result: any, license: any) => {
    const newResult = result;
    newResult[license.code] = license;
    return newResult;
  },
  {}
);

export interface License {
  uuid: string;
  license: { code: string; name: string; uuid: string; is_add_on: boolean };
  total_seats: number;
  used_seats: number;
}

export const UiFlags: any = {
  tableHeaders: 'pl-ui-table-headers',
  userEditHeader: 'pl-ui-user-edit-header',
};

@Injectable({ providedIn: 'root' })
export class AccountsService {
  constructor(
    private httpClient: HttpClient,
    private dataServiceConfig: DefaultDataServiceConfig
  ) {}

  fetchAccounts() {
    const accountsUrl = `${this.dataServiceConfig.root}/accounts/`;
    return this.httpClient.get(accountsUrl);
  }

  fetchAccountUsers(accountUuid: string) {
    const usersUrl = `${this.dataServiceConfig.root}/accounts/${accountUuid}/users/`;
    return this.httpClient.get(usersUrl);
  }

  fetchAccountKangaUser(accountUuid: string, userUuid: string) {
    const userUrl = `${this.dataServiceConfig.root}/accounts/${accountUuid}/users/${userUuid}`;
    return this.httpClient.get(userUrl);
  }

  fetchAccountLicenses(accountUuid: string) {
    const licensesUrl = `${this.dataServiceConfig.root}/accounts/${accountUuid}/licenses/`;
    return this.httpClient.get(licensesUrl);
  }

  getLicenseCounts(licenses: any[]) {
    const types: any = [];
    const results = licenses.reduce((accumulator: any, L: License) => {
      const code = L.license.code;
      types.push(code);
      accumulator[`${code}`] = {
        license: L.license,
        purchased: L.total_seats,
        used: L.used_seats,
        remaining: L.total_seats - L.used_seats,
        fraction: `${L.used_seats}/${L.total_seats}`,
        percentUsed: Math.floor((L.used_seats / L.total_seats) * 100),
        is_addOn: L.license.is_add_on,
        avatar: LicenseTypeMap[code] ? LicenseTypeMap[code].avatar : '?',
      };
      return accumulator;
    }, {});
    results.types = [...Object.keys(LicenseTypeMap)].filter((t: string) =>
      types.includes(t)
    );
    return results;
  }

  getLicenseCountsInfo(licenses: any) {
    const licenseCounts: any = {};

    const licenseInfo = this.getLicenseCounts(licenses);
    licenseInfo.types.forEach((type: string) => {
      licenseCounts[type] = {
        purchased: licenseInfo[type].purchased || 0,
        remaining: licenseInfo[type].remaining || 0,
        is_addOn: licenseInfo[type].is_addOn,
      };
    });
    licenseCounts.types = licenseInfo.types;
    licenseCounts.totalRemaining = 0;
    licenseCounts.totalRemarkableReading = 0;
    licenseInfo.types.forEach((type: string) => {
      if (type === 'remarkable_reading') {
        licenseCounts.totalRemarkableReading += licenseCounts[type].purchased;
      }
      if (!licenseCounts[type].is_addOn) {
        licenseCounts.totalRemaining += licenseCounts[type].remaining || 0;
      }
    });

    return licenseCounts;
  }

  save(user: User, accountUuid: string): Observable<User> {
    const method = user.uuid ? 'put' : 'post';
    const suffix = user.uuid ? `${user.uuid}/` : '';
    const url = `${this.dataServiceConfig.root}/accounts/${accountUuid}/users/${suffix}`;
    return (
      this.httpClient[method] as (url: string, body: any) => Observable<User>
    )(url, {
      ...user,
    });
  }
}
